import { MANUAL } from "./conteudo.js";

/**
 * A busca do manual.
 *
 * Ajuda que não se busca não é ajuda: ninguém lê vinte tópicos para descobrir
 * como marcar um documento como ilegível. Aqui a varredura é sobre o CONTEÚDO
 * inteiro — título do tópico, título da seção, parágrafo, passo, item de lista,
 * legenda de imagem, célula de tabela e atalho —, e o resultado devolve o
 * trecho onde a palavra apareceu, para a pessoa reconhecer a resposta antes de
 * clicar.
 *
 * Fica fora do componente de propósito: é função pura, testável sem montar
 * tela, e é a mesma que a paleta (Ctrl+K) usa para oferecer ajuda junto com as
 * áreas e os clientes.
 */

/** "conferencia" acha "conferência" — acento não pode esconder resposta. */
export const semAcento = (texto) =>
  String(texto ?? "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase();

/** Todo o texto de um bloco, em pedaços — é o que a busca varre. */
function textoDoBloco(bloco) {
  switch (bloco.t) {
    case "p":
      return [bloco.texto];
    case "passos":
    case "itens":
      return bloco.itens;
    case "imagem":
    case "video":
      return [bloco.legenda];
    case "aviso":
      return [bloco.texto];
    case "atalhos":
      return bloco.itens.flatMap((i) => [i.teclas, i.oque]);
    case "tabela":
      return [...bloco.cabecalho, ...bloco.linhas.flat()];
    case "link":
      return [bloco.rotulo, bloco.explica];
    default:
      return [];
  }
}

/**
 * O manual achatado: uma linha por seção, com o texto todo junto.
 *
 * Montado uma vez, no carregamento do módulo. São dezenas de seções, não
 * milhares — indexar de novo a cada tecla digitada seria trabalho jogado fora.
 */
export const INDICE = MANUAL.flatMap((topico) =>
  topico.secoes.map((secao) => {
    const pedacos = secao.blocos.flatMap(textoDoBloco).filter(Boolean);
    return {
      topico: topico.id,
      tituloDoTopico: topico.titulo,
      icone: topico.icone,
      secao: secao.id,
      titulo: secao.titulo,
      pedacos,
      /* Tudo em minúsculas e sem acento, uma vez só: é contra este campo que a
         busca corre. */
      procuravel: semAcento([topico.titulo, topico.resumo, secao.titulo, ...pedacos].join(" \n ")),
    };
  }),
);

/** O trecho em volta da palavra, para a pessoa reconhecer a resposta. */
function trechoCom(pedacos, termo, tamanho = 150, formas = [termo]) {
  for (const pedaco of pedacos) {
    const limpo = semAcento(pedaco);
    const onde =
      formas
        .map((f) => limpo.indexOf(semAcento(f)))
        .filter((i) => i >= 0)
        .toSorted((a, b) => a - b)[0] ?? -1;
    if (onde < 0) continue;
    const inicio = Math.max(0, onde - 45);
    const corte = pedaco.slice(inicio, inicio + tamanho).trim();
    return `${inicio > 0 ? "…" : ""}${corte}${inicio + tamanho < pedaco.length ? "…" : ""}`;
  }
  return pedacos[0]?.slice(0, tamanho) ?? "";
}

/**
 * As formas de uma palavra que valem a mesma busca.
 *
 * Português quebra a busca literal no plural mais comum do escritório:
 * "permissao" não casa com "permissões", "acao" não casa com "ações". Não é
 * lugar de radicalizador de verdade — são três trocas que cobrem o que se
 * digita aqui, e mais que isso começaria a achar o que ninguém pediu.
 */
export function variantes(palavra) {
  const formas = new Set([palavra]);
  if (palavra.endsWith("ao")) formas.add(`${palavra.slice(0, -2)}oes`);
  if (palavra.endsWith("oes")) formas.add(`${palavra.slice(0, -3)}ao`);
  if (palavra.endsWith("s")) formas.add(palavra.slice(0, -1));
  else formas.add(`${palavra}s`);
  return [...formas];
}

/**
 * Procura no manual inteiro.
 *
 * A ordem privilegia o que casa no TÍTULO: quem digita "conferir" quer a seção
 * de conferência, não o parágrafo de outra tela que menciona a palavra. Termos
 * separados por espaço são exigidos todos (E, não OU) — busca que devolve tudo
 * é o mesmo que busca nenhuma.
 */
export function procurar(termo, limite = 20) {
  const limpo = semAcento(termo).trim();
  if (limpo.length < 2) return [];
  const palavras = limpo.split(/\s+/);

  const achados = [];
  for (const linha of INDICE) {
    if (!palavras.every((palavra) => variantes(palavra).some((forma) => linha.procuravel.includes(forma)))) continue;
    const noTitulo = semAcento(linha.titulo).includes(limpo);
    const noTopico = semAcento(linha.tituloDoTopico).includes(limpo);
    achados.push({
      ...linha,
      peso: (noTitulo ? 100 : 0) + (noTopico ? 40 : 0) + (linha.procuravel.split(limpo).length - 1),
      trecho: trechoCom([linha.titulo, ...linha.pedacos], palavras[0], undefined, variantes(palavras[0])),
    });
  }
  return achados.toSorted((a, b) => b.peso - a.peso).slice(0, limite);
}

/** O tópico pelo id, para a tela abrir direto no endereço `/ajuda/<id>`. */
export const topicoPorId = (id) => MANUAL.find((t) => t.id === id) ?? null;

/** Quantas seções o manual tem — a tela diz isso na abertura. */
export const TAMANHO = { topicos: MANUAL.length, secoes: INDICE.length };
